/**
 * XMLUI Navigation Component
 * 
 * This component provides the main navigation with:
 * - Dashboard, Transactions, Planning, Insights, Reporting, Settings
 * - Active state management
 * - Responsive design
 * - Accessibility compliance (WCAG 2.2 AA)
 * - Keyboard navigation support
 * 
 * Features:
 * - Semantic navigation structure
 * - Focus management
 * - Mobile-responsive
 * - Active route highlighting
 * 
 * @see .specify/memory/constitution.md
 * @see /Reference/Reference design-review/design-principles.md
 */

import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useNavigationGuardState } from '../../pages/settings/useNavigationGuardState';
import { useMobileMenu } from '../../contexts/MobileMenuContext';
import { useMojoAgent } from '../../contexts/MojoAgentContext';
import { 
  LayoutDashboard,
  CreditCard,
  Wallet,
  PiggyBank,
  TrendingUp,
  FileText
} from 'lucide-react';

interface NavigationItem {
  name: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  description: string;
}

const navigationItems: NavigationItem[] = [
  {
    name: 'Dashboard',
    href: '/dashboard',
    icon: LayoutDashboard,
    description: 'Overview of your financial situation',
  },
  {
    name: 'Transactions',
    href: '/transactions',
    icon: CreditCard,
    description: 'View and manage your transactions',
  },
  {
    name: 'Accounts',
    href: '/accounts',
    icon: Wallet,
    description: 'Manage your financial accounts',
  },
  {
    name: 'Planning',
    href: '/budgets',
    icon: PiggyBank,
    description: 'Plan budgets, savings goals, and bills',
  },
  {
    name: 'Insights',
    href: '/insights',
    icon: TrendingUp,
    description: 'AI-powered financial insights',
  },
  {
    name: 'Reporting',
    href: '/reporting',
    icon: FileText,
    description: 'Generate financial reports',
  },
];

/**
 * Navigation Component
 * 
 * Provides the main navigation menu with all application sections.
 */
export function Navigation(): React.ReactElement {
  const location = useLocation();
  const navigate = useNavigate();
  const { hasUnsavedChanges, onNavigationAttempt } = useNavigationGuardState();
  const { isMobileMenuOpen, setIsMobileMenuOpen } = useMobileMenu();
  const { compactNavigation } = useMojoAgent();
  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(true);
  const lastScrollY = useRef(0);
  const navRef = useRef<HTMLElement>(null);

  const isActiveRoute = (href: string): boolean => {
    if (href === '/dashboard') {
      return location.pathname === '/dashboard' || location.pathname === '/';
    }
    return location.pathname.startsWith(href);
  };

  const handleNavigationClick = (href: string, e: React.MouseEvent) => {
    e.preventDefault();
    const isActive = isActiveRoute(href);
    
    // Only check guard if navigating away from current page
    if (hasUnsavedChanges && !isActive) {
      onNavigationAttempt(href);
    } else {
      navigate(href);
    }
  };

  // Scroll menu into view when opened, accounting for sticky header
  useEffect(() => {
    if (isMobileMenuOpen && mobileMenuRef.current) {
      const menuEl = mobileMenuRef.current;
      const rect = menuEl.getBoundingClientRect();
      const scrollY = window.scrollY;
      const viewportHeight = window.innerHeight;
      const headerEl = document.querySelector('header[role="banner"]');
      const headerHeight = headerEl ? headerEl.getBoundingClientRect().height : 0;
      
      // Check if menu is out of viewport (accounting for header)
      if (rect.top < headerHeight || rect.bottom > viewportHeight) {
        // Calculate target scroll position: menu's absolute position minus header height
        const menuAbsoluteTop = scrollY + rect.top;
        const targetScrollY = menuAbsoluteTop - headerHeight;
        window.scrollTo({ top: targetScrollY, behavior: 'smooth' });
      }
    }
  }, [isMobileMenuOpen]);

  // Handle scroll-based show/hide of navigation bar
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const scrollThreshold = 10; // Minimum scroll distance to trigger hide/show
      
      // Only hide/show if scrolled past threshold
      if (Math.abs(currentScrollY - lastScrollY.current) < scrollThreshold) {
        return;
      }

      // Show navigation when scrolling up, hide when scrolling down
      if (currentScrollY < lastScrollY.current) {
        // Scrolling up - show navigation
        setIsVisible(true);
      } else if (currentScrollY > lastScrollY.current && currentScrollY > 100) {
        // Scrolling down and past initial 100px - hide navigation
        setIsVisible(false);
      } else if (currentScrollY <= 100) {
        // Near top of page - always show navigation
        setIsVisible(true);
      }

      lastScrollY.current = currentScrollY;
    };

    // Set initial scroll position
    lastScrollY.current = window.scrollY;

    window.addEventListener('scroll', handleScroll, { passive: true });
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <nav 
      ref={navRef}
      className={`
        bg-white border-b border-gray-200 dark:border-gray-700 dark:bg-gray-900
        sticky top-16 z-50
        transition-transform duration-300 ease-in-out
        ${isVisible ? 'translate-y-0' : '-translate-y-full'}
      `}
      role="navigation"
      aria-label="Main navigation"
    >
      <div className="w-full min-w-0 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Desktop/Tablet Navigation Bar - Hidden on mobile */}
        <div
          className={`${
            compactNavigation ? 'flex h-16' : 'hidden md:flex h-12 md:h-16 lg:h-12'
          } items-center justify-between`}
        >
          {/* Desktop Navigation (lg+) - Horizontal: Icon and Text */}
          <div
            className={`${compactNavigation ? 'hidden' : 'hidden lg:flex'} items-center space-x-1`}
            data-testid="desktop-navigation"
          >
            {navigationItems.map((item) => {
              const Icon = item.icon;
              const isActive = isActiveRoute(item.href);
              
              return (
                <button
                  key={item.name}
                  type="button"
                  onClick={(e) => handleNavigationClick(item.href, e)}
                  className={`
                    nav-item flex items-center space-x-2 px-3 py-2 text-sm font-medium rounded-md whitespace-nowrap transition-colors
                    ${isActive 
                      ? 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20' 
                      : 'text-gray-700 dark:text-gray-100 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700/50'
                    }
                  `}
                  aria-current={isActive ? 'page' : undefined}
                  title={item.description}
                >
                  <Icon className="h-4 w-4 flex-shrink-0" />
                  <span className="whitespace-nowrap">{item.name}</span>
                </button>
              );
            })}
          </div>

          {/* Tablet Navigation (md to lg) - Vertical: Icon above Text */}
          <div
            className={`${
              compactNavigation ? 'flex' : 'hidden md:flex lg:hidden'
            } items-center justify-center space-x-1 overflow-x-auto w-full min-w-0`}
            data-testid="tablet-navigation"
          >
            {navigationItems.map((item) => {
              const Icon = item.icon;
              const isActive = isActiveRoute(item.href);
              
              return (
                <button
                  key={item.name}
                  type="button"
                  onClick={(e) => handleNavigationClick(item.href, e)}
                  className={`
                    nav-item flex flex-col items-center justify-center space-y-1 px-2 py-1.5 text-xs font-medium rounded-md min-w-[50px] h-full transition-colors
                    ${isActive 
                      ? 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20' 
                      : 'text-gray-700 dark:text-gray-100 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700/50'
                    }
                  `}
                  aria-current={isActive ? 'page' : undefined}
                  title={item.description}
                >
                  <Icon className="h-5 w-5 flex-shrink-0" />
                  <span className="leading-tight text-center whitespace-nowrap">{item.name}</span>
                </button>
              );
            })}
          </div>

        </div>

        {/* Mobile Navigation Menu (Hamburger Menu) */}
        {isMobileMenuOpen && (
          <div 
            ref={mobileMenuRef}
            data-testid="mobile-navigation"
            className="md:hidden"
          >
            <div className="px-2 pt-2 pb-3 space-y-1 bg-gray-50 dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700">
              {navigationItems.map((item) => {
                const Icon = item.icon;
                const isActive = isActiveRoute(item.href);
                
                return (
                  <button
                    key={item.name}
                    type="button"
                    onClick={(e) => {
                      handleNavigationClick(item.href, e);
                      setIsMobileMenuOpen(false);
                    }}
                    className={`
                      mobile-nav-item flex items-center space-x-3 w-full text-left px-3 py-2 text-sm font-medium rounded-md transition-colors
                      ${isActive 
                        ? 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20' 
                        : 'text-gray-700 dark:text-gray-100 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700/50'
                      }
                    `}
                    aria-current={isActive ? 'page' : undefined}
                    title={item.description}
                  >
                    <Icon className="h-5 w-5" />
                    <span>{item.name}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}
